/**
 * Winter Soldier — „Marvel: For King and Country".
 *
 * BRAINWASHED: efekty jeho kariet sa nedajú zrušiť (`cardsUncancelable`).
 * Efekty označené {RED ROOM} sú na jeho škodu alebo v prospech súpera — A Boy
 * Named Bucky ich do konca ťahu vypne (`turn.calm`).
 */

import { areAdjacent, reachFrom } from '../map.js';
import type { Ctx } from '../ops.js';
import {
  askChoice,
  dealDamage,
  discardRandom,
  discardToDeck,
  drawCard,
  emit,
  gainAction,
  getMap,
  getPlayer,
  healFighter,
  shuffleDeck,
} from '../ops.js';
import { areEnemies } from '../players.js';
import type { PlayerIndex } from '../types.js';
import { feint } from './common.js';
import { askBoost, canMove, fighterOrNull, opponentOf } from './helpers.js';
import { wilyFighting } from './robinHood.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const SOLDIER = 'winter-soldier';
const MANIPULATION = 'winter-soldier.manipulation';
const REPROGRAM = 'winter-soldier.reprogram';
const REMORSE = 'winter-soldier.without-remorse';

const soldierOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === SOLDIER) ?? null;

/** Platí efekt {RED ROOM}? Nie, keď ho A Boy Named Bucky v tomto ťahu vypol. */
const redRoom = (ctx: Ctx, player: PlayerIndex) => !ctx.state.turn.calm?.includes(player);

/** „Ignore any {RED ROOM} effects on your cards for the rest of your turn. Gain 1 action." */
const aBoyNamedBucky: CardEffect = {
  scheme: (e) => {
    (e.ctx.state.turn.calm ??= []).push(e.player);
    emit(e.ctx, { type: 'specialUsed', player: e.player, heroId: SOLDIER, name: 'A Boy Named Bucky: efekty RED ROOM sa do konca ťahu ignorujú' });
    gainAction(e.ctx, e.player);
  },
};

/**
 * „DURING: If Winter Soldier is adjacent to the opposing fighter, the value of
 * this card is 6 instead. AFTER: {RED ROOM} Your opponent moves each of their
 * fighters up to 5 spaces."
 */
const bionicArm: CardEffect = {
  during: (e, value) => {
    const me = soldierOf(e.ctx, e.player);
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    return me && them && areAdjacent(getMap(e.ctx), me.spaceId, them.spaceId) ? 6 : value;
  },
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null || !redRoom(e.ctx, e.player)) return;
    for (const f of e.ctx.state.fighters.filter((x) => x.owner === opponent)) {
      if (!canMove(e.ctx, f.id, 5, false)) continue;
      askChoice(e.ctx, {
        kind: 'move',
        player: opponent,
        source: e.cardId,
        label: `Bionic Arm: smieš presunúť ${f.name} až o 5`,
        optional: true,
        fighters: [f.id],
        max: 5,
        through: false,
      });
    }
  },
};

/** „DURING: You may BOOST this card. AFTER: Deal damage to the opposing fighter equal to the amount of combat damage you took." */
const completeTheMission: CardEffect = {
  duringChoice: (e) => askBoost(e, 'Complete the Mission: smieš boostovať'),
  after: (e) => {
    if (e.opponentFighterId && e.damage) dealDamage(e.ctx, e.opponentFighterId, e.damage);
  },
};

/**
 * „Draw until you have 5 cards in your hand. {RED ROOM} Each opponent may draw
 * until they have 5 cards in their hand." Schéma v ruke ešte leží — neráta sa.
 */
const manipulation: CardEffect = {
  scheme: (e) => {
    const seat = getPlayer(e.ctx.state, e.player);
    while (seat.hand.length - 1 < 5 && seat.deck.length > 0) drawCard(e.ctx, e.player);
    if (!redRoom(e.ctx, e.player)) return;
    e.ctx.state.players.forEach((other, index) => {
      if (other.eliminated || other.hand.length >= 5 || !areEnemies(e.ctx.state, index, e.player)) return;
      askChoice(e.ctx, {
        kind: 'confirm',
        player: index,
        source: e.cardId,
        label: 'Manipulation: smieš dotiahnuť do 5 kariet',
        optional: true,
        effect: MANIPULATION,
        yes: 'Dotiahnuť do 5',
        no: 'Nie',
      });
    });
  },
  onConfirm: (ctx, choice) => {
    const seat = getPlayer(ctx.state, choice.player);
    while (seat.hand.length < 5 && seat.deck.length > 0) drawCard(ctx, choice.player);
  },
};

/** „Add +1 to this card's value for each space between you and the opposing fighter along the shortest path." */
const marksman: CardEffect = {
  during: (e, value) => {
    const me = fighterOrNull(e.ctx, e.fighterId);
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (!me || !them) return value;
    const map = getMap(e.ctx);
    const steps = reachFrom(map, me.spaceId, map.spaces.length).distance.get(them.spaceId);
    return steps === undefined ? value : value + Math.max(0, steps - 1);
  },
};

/** „{RED ROOM} If you won the combat, take 2 damage." */
const programmedToKill: CardEffect = {
  after: (e) => {
    const me = soldierOf(e.ctx, e.player);
    if (e.won && me && redRoom(e.ctx, e.player)) dealDamage(e.ctx, me.id, 2);
  },
};

/** „{RED ROOM} Discard 2 random cards, then draw 2 cards." */
const reflexMemories: CardEffect = {
  after: (e) => {
    if (!redRoom(e.ctx, e.player)) return;
    discardRandom(e.ctx, e.player);
    discardRandom(e.ctx, e.player);
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
  },
};

/** „Choose 3 cards in your discard pile and shuffle them into your deck." Karty zo súboja v odhode ešte nie sú. */
function askReprogram(ctx: Ctx, player: PlayerIndex, source: string, left: number): void {
  const discard = getPlayer(ctx.state, player).discard;
  if (left <= 0 || discard.length === 0) {
    shuffleDeck(ctx, player);
    return;
  }
  askChoice(ctx, {
    kind: 'card',
    player,
    source,
    label: `Reprogram: vyber kartu z odhodu, ktorá sa zamieša do balíčka (${4 - left} z 3)`,
    optional: false,
    from: 'discard',
    cards: discard.slice(),
    purpose: 'effect',
    left,
  });
}

const reprogram: CardEffect = {
  after: (e) => askReprogram(e.ctx, e.player, e.cardId, 3),
  onCardChosen: (ctx, choice, cardId) => {
    discardToDeck(ctx, choice.player, cardId);
    askReprogram(ctx, choice.player, choice.source ?? cardId, (choice.left ?? 1) - 1);
  },
};

/** „{RED ROOM} Your opponent may draw 1 card." */
const withoutRemorse: CardEffect = {
  after: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null || !redRoom(e.ctx, e.player)) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: opponent,
      source: e.cardId,
      label: 'Without Remorse: smieš si potiahnuť 1 kartu',
      optional: true,
      effect: REMORSE,
      yes: 'Potiahnuť',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => void drawCard(ctx, choice.player),
};

/** „If you won the combat, Winter Soldier recovers 2 health." */
const bornInTheBarracks: CardEffect = {
  after: (e) => {
    const me = soldierOf(e.ctx, e.player);
    if (e.won && me) healFighter(e.ctx, me.id, 2);
  },
};

export const winterSoldierCards: EffectRegistry = {
  'winter-soldier.a-boy-named-bucky': aBoyNamedBucky,
  'winter-soldier.bionic-arm': bionicArm,
  'winter-soldier.complete-the-mission': completeTheMission,
  'winter-soldier.feint': feint,
  [MANIPULATION]: manipulation,
  'winter-soldier.marksman': marksman,
  'winter-soldier.programmed-to-kill': programmedToKill,
  'winter-soldier.reflex-memories': reflexMemories,
  [REPROGRAM]: reprogram,
  'winter-soldier.wily-fighting': wilyFighting,
  [REMORSE]: withoutRemorse,
  'winter-soldier.born-in-the-barracks': bornInTheBarracks,
};

export const winterSoldierSpecial: HeroSpecial = {
  name: 'Brainwashed',
  /** „Effects on Winter Soldier's cards cannot be canceled." */
  cardsUncancelable: () => true,
};
